import { EmailHistory } from '../email/EmailHistory';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import WarehouseRounded from '@mui/icons-material/WarehouseRounded';
import { Alert, Box, Button, Link, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { Money, Qty } from '../../components/Money';
import { businessDate, dateTime } from '../../lib/format';
import { goodsReceiptDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  purchaseApi,
  useGoodsReceipt,
  usePurchaseOrder,
  usePurchasesMutation,
  type GoodsReceipt,
  type GoodsReceiptLine,
} from './api';
import {
  DetailSkeleton,
  DocHeader,
  ErrorState,
  Facts,
  ReasonDialog,
  ReceiptStatusChip,
  Section,
  SummaryCard,
  SummaryGrid,
} from './components/ui';
import { compare, normalize } from './lib/decimal';
import { inLineUnit } from './lib/progress';
import { MoreMenu } from '../../components/MoreMenu';
import { useLinked, useLinkedOne } from '../../components/useLinked';

export function GoodsReceiptDetailPage() {
  const { id } = useParams();
  const receipt = useGoodsReceipt(id);
  if (receipt.isLoading) return <DetailSkeleton />;
  if (receipt.error || !receipt.data)
    return <ErrorState error={receipt.error} onRetry={() => void receipt.refetch()} />;
  return <ReceiptDetail gr={receipt.data} />;
}

function ReceiptDetail({ gr }: { gr: GoodsReceipt }) {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const [reversing, setReversing] = useState(false);
  const order = usePurchaseOrder(gr.purchaseOrderId ?? undefined);
  const linkedOrder = useLinkedOne('purchaseOrder', gr.purchaseOrderId);
  const bills = useLinked('bills', { goodsReceiptId: gr.id });
  const returns = useLinked('purchaseReturns', { goodsReceiptId: gr.id });
  const showCost = can('products.view_cost');
  const posted = gr.status === 'POSTED';
  const toBill = gr.lines.some((l) => compare(l.qtyToBill, '0') > 0);
  const billed = gr.lines.some((l) => compare(l.qtyBilled, '0') > 0);
  const rejected = gr.lines.filter((l) => compare(l.qtyRejected, '0') > 0);

  const reverse = usePurchasesMutation((reason: string) =>
    purchaseApi.reverseReceipt(gr.id, gr.version, reason),
  );

  const columns: Column<GoodsReceiptLine>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (l) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {l.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {l.sku}
          </Typography>
          {compare(l.qtyRejected, '0') > 0 && (
            <Typography variant="caption" color="warning.main" component="div">
              {normalize(inLineUnit(l.qtyRejected, l.factor))} {l.unitSymbol} rejected
              {l.rejectReason ? ` — ${l.rejectReason}` : ''}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'qty',
      header: 'Accepted',
      align: 'right',
      render: (l) => <Qty value={l.qty} unit={l.unitSymbol} />,
    },
    ...(showCost
      ? [
          {
            key: 'cost',
            header: 'Unit cost',
            align: 'right' as const,
            hideBelow: 'md' as const,
            render: (l: GoodsReceiptLine) => <Money value={l.unitPrice} />,
          },
          {
            key: 'total',
            header: 'Value',
            align: 'right' as const,
            render: (l: GoodsReceiptLine) => <Money value={l.totalCost} strong />,
          },
        ]
      : []),
    {
      key: 'bill',
      header: 'To bill',
      align: 'right',
      hideBelow: 'sm',
      render: (l) =>
        compare(l.qtyToBill, '0') > 0 ? (
          <Typography variant="body2" sx={{ fontWeight: 650, color: 'info.main' }}>
            <Qty value={l.qtyToBill} />
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            Billed
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      <DocHeader
        back={
          gr.purchaseOrderId
            ? {
                to: `/purchases/orders/${gr.purchaseOrderId}`,
                label: order.data?.number ?? 'Purchase order',
              }
            : { to: '/purchases?tab=receipts', label: 'Goods receipts' }
        }
        eyebrow="Goods receipt"
        title={gr.number}
        chips={<ReceiptStatusChip status={gr.status} />}
        subtitle={
          <>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {gr.contact.name}
            </Box>
            {' · '}
            {businessDate(gr.docDate, format)}
            {gr.supplierRef ? ` · Delivery note ${gr.supplierRef}` : ''}
          </>
        }
        actions={
          <>
            <WhatsappA4
              type="goods_receipt"
              id={gr.id}
              number={gr.number}
              contact={gr.contact}
              vars={{ date: businessDate(gr.docDate, format) }}
              doc={() => goodsReceiptDoc(gr, format)}
            />
            {posted && can('purchases.cancel') && (
              <Tooltip title={billed ? 'Cancel its bill first' : ''}>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<UndoRounded />}
                    disabled={billed}
                    onClick={() => setReversing(true)}
                  >
                    Reverse
                  </Button>
                </span>
              </Tooltip>
            )}
            {posted && toBill && can('purchases.bill') && (
              <Button
                variant="contained"
                startIcon={<ReceiptLongRounded />}
                component={RouterLink}
                to={`/purchases/bills/new?receiptId=${gr.id}`}
              >
                Record bill
              </Button>
            )}
            <MoreMenu
              linked={[linkedOrder, bills, returns]}
              exportModel="goods_receipt"
              exportId={gr.id}
            />
          </>
        }
      />

      {gr.status === 'REVERSED' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Reversed {dateTime(gr.reversedAt, format)}
          {gr.reverseReason ? ` — ${gr.reverseReason}` : ''}. The goods left stock again.
        </Alert>
      )}

      <SummaryGrid>
        {showCost && (
          <SummaryCard label="Stock value" icon={<WarehouseRounded />}>
            <Money value={gr.totalCost} />
          </SummaryCard>
        )}
        <SummaryCard label="Items" icon={<CategoryRounded />} accent="info">
          {gr.lines.length}
          {rejected.length > 0 && (
            <Typography variant="caption" color="warning.main" component="div">
              {rejected.length} with rejects
            </Typography>
          )}
        </SummaryCard>
        <SummaryCard label="Location" icon={<PlaceRounded />} accent="success">
          {gr.location.name}
        </SummaryCard>
        <SummaryCard label="Billing" icon={<DescriptionRounded />} accent="warning">
          {!posted ? '—' : toBill ? (billed ? 'Partly billed' : 'Not billed') : 'Fully billed'}
        </SummaryCard>
      </SummaryGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <DataTable columns={columns} rows={gr.lines} rowKey={(l) => l.id} />
        </Box>
        <Section title="Details">
          <Facts
            items={[
              ['Supplier', gr.contact.name],
              [
                'Source',
                gr.purchaseOrderId ? (
                  <Link component={RouterLink} to={`/purchases/orders/${gr.purchaseOrderId}`}>
                    {order.data?.number ?? 'Purchase order'}
                  </Link>
                ) : (
                  'Direct receipt'
                ),
              ],
              ['Posted', dateTime(gr.postedAt, format)],
            ]}
          />
          {gr.notes && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
            >
              {gr.notes}
            </Typography>
          )}
        </Section>
      </Box>

      <ReasonDialog
        open={reversing}
        title={`Reverse ${gr.number}?`}
        body={
          <>
            The goods leave stock at {gr.location.name} again and the amount owed for them is
            cleared. {gr.purchaseOrderId ? 'They become outstanding on the order again.' : ''}
          </>
        }
        confirmLabel="Reverse receipt"
        onConfirm={(reason) =>
          reverse.mutateAsync(reason).then(() => notify.success(`${gr.number} reversed`))
        }
        onClose={() => setReversing(false)}
      />
      <AttachmentsPanel recordType="GOODS_RECEIPT" recordId={gr.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="goods_receipt" documentId={gr.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="goods_receipt" documentId={gr.id} sx={{ mt: 3 }} />
    </Box>
  );
}
